import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Car, MapPin, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AuthPromptDialog } from "@/components/auth/AuthPromptDialog";

const Hero = () => {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setIsAuthed(!!session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthed(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleAction = (path: string) => {
    if (isAuthed) {
      navigate(path);
    } else {
      setPendingPath(path);
      setAuthOpen(true);
    }
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary-800 to-primary-900 text-white">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMikiLz48L2c+PC9zdmc+')] opacity-20 animate-float" />

      <div className="absolute -bottom-16 right-0 left-0 h-16 bg-white/10 backdrop-blur-md transform -skew-y-3"></div>

      <div className="container mx-auto px-4 py-24 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-slide-in-bottom mb-4">
            <span className="inline-block py-1 px-3 rounded-full text-xs font-semibold bg-blue-400/20 text-blue-100">
              Bayzo Solution
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-8 animate-fade-in">
            Find Your Perfect
            <span className="bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent animate-pulse-glow"> Parking Spot</span>
          </h1>

          <p className="text-xl md:text-2xl mb-12 text-blue-100 animate-slide-in-bottom opacity-90 delay-100">
            Discover secure parking spaces near you. Book instantly and park worry-free.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="animate-bounce-in" style={{ animationDelay: "0.3s" }}>
              <Button
                size="lg"
                onClick={() => handleAction("/search")}
                className="bg-white text-primary-800 hover:bg-blue-50 font-semibold px-8 py-6 flex items-center gap-2 shadow-lg hover:shadow-xl transition-all group"
              >
                <MapPin className="w-5 h-5 group-hover:animate-pulse" />
                Find Parking
                <ArrowRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="animate-bounce-in" style={{ animationDelay: "0.6s" }}>
              <Button
                size="lg"
                variant="outline"
                onClick={() => handleAction("/list-spot")}
                className="border-2 border-white bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-8 py-6 flex items-center gap-2 group"
              >
                <Car className="w-5 h-5 group-hover:animate-pulse" />
                List Your Spot
                <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" />
              </Button>
            </div>
          </div>

          <div className="absolute -top-10 left-10 w-20 h-20 bg-blue-400/10 rounded-full blur-xl animate-float" style={{ animationDelay: "1s" }}></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-300/10 rounded-full blur-xl animate-float" style={{ animationDelay: "2s" }}></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-blue-500/10 rounded-full blur-lg animate-float" style={{ animationDelay: "0.5s" }}></div>
        </div>
      </div>

      <AuthPromptDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        title="Sign in to continue"
        description="Sign in or create an account to find or list parking spots."
        onAuthSuccess={() => {
          if (pendingPath) navigate(pendingPath);
        }}
      />
    </div>
  );
};

export default Hero;
